'use client';

import { ReactNode, useActionState, useEffect, useMemo, useState } from 'react';
import { useTranslations } from 'next-intl';
import clsx from 'clsx';
import { RATING_REVIEWS_FORM_ID } from '@headless-workspace/config';
import { CommonDI, LoggerType } from '@headless-workspace/domain/common/DI';
import {
    ConnectedUserInfo,
    FormUtils,
    generateDefaultNickname,
    RatingAndReviewsFormValue,
    UserConnectionStatus,
} from '@headless-workspace/domain/common/server';
import {
    GlowButton,
    GlowDivider,
    GlowDropDown,
    GlowInput,
    GlowModal,
    GlowPillsWrapper,
    GlowStarRating,
    GlowTextArea,
    GlowTextBody,
    Icons,
    Pill,
    PropsWithLocale,
} from '@headless-workspace/glow-ds';
import { Component, NoEmptyArray } from '@headless-workspace/typings';
import { getFieldErrorKey } from '@headless-workspace/utils';
import { ZodSchema } from 'zod';
import { ReviewPayload } from '../../../../server';

type RatingsAndReviewsFormState = {
    data: { [k: keyof RatingAndReviewsFormValue['fields']]: FormDataEntryValue | undefined };
    errors: undefined | Record<string, string[]>;
    success: boolean;
};

type RatingsAndReviewsFormProps = PropsWithLocale & {
    defaultEmail?: string;
    formInfo: RatingAndReviewsFormValue['fields'];
    userInfo: ConnectedUserInfo;
    reviewPayload?: ReviewPayload;
    onSubmit: (isSuccess: boolean, data: ReviewPayload) => void;
    ProductInfos: ReactNode;
};

const REVIEW_TITLE_MIN_LENGTH = 2;
const REVIEW_TITLE_MAX_LENGTH = 50;
const REVIEW_NICKNAME_MIN_LENGTH = 2;
const REVIEW_NICKNAME_MAX_LENGTH = 25;
const REVIEW_DETAILS_MAX_LENGTH = 1000;
const REVIEW_STAR_RATING_MIN_LENGTH = 1;
const REVIEW_STAR_RATING_MAX_LENGTH = 5;

const FORM_SCHEMA_NOT_INITIALIZED_ERROR = 'formSchema is not initialized';
const FIELDS_KEYS = ['age', 'content', 'nickname', 'rating', 'recommended', 'title', 'usageDuration'] as const;

const DEFAULT_PILL_ID = 'empty';
const PILL_LIKE_ID = 'like';
const PILL_DISLIKE_ID = 'dislike';

const getDefaultPillSelected = (isRecommended?: boolean) => {
    if (typeof isRecommended === 'boolean') {
        return isRecommended ? PILL_LIKE_ID : PILL_DISLIKE_ID;
    }

    return DEFAULT_PILL_ID; // FIXME: hack to avoid having a default value. Should update GlowPills to support no default value for radio type
};

export const RatingsAndReviewsForm: Component<RatingsAndReviewsFormProps> = ({
    formInfo,
    userInfo,
    reviewPayload,
    onSubmit,
    ProductInfos,
}) => {
    const t = useTranslations('Discover.Pdp.ratingAndReviewsForm');

    const pills: NoEmptyArray<Pill> = [
        { id: PILL_LIKE_ID, label: t('label.like'), icon: Icons.Like },
        { id: PILL_DISLIKE_ID, label: t('label.dislike'), icon: Icons.Dislike },
    ];

    const defaultNickname = generateDefaultNickname(userInfo);

    const initialValue: RatingsAndReviewsFormState = useMemo(
        () => ({
            data: {
                age: reviewPayload?.age,
                content: reviewPayload?.content,
                nickname:
                    userInfo.status === UserConnectionStatus.Registered ? defaultNickname : reviewPayload?.nickName,
                rating: reviewPayload?.rating?.toString(),
                recommended: reviewPayload?.isRecommended?.toString(),
                title: reviewPayload?.title,
                usageDuration: reviewPayload?.usageDuration,
            },
            errors: undefined,
            success: false,
        }),
        [userInfo, reviewPayload, defaultNickname],
    );

    const [formSchema, setFormSchema] = useState<ZodSchema | null>(null);
    const [starRating, setStarRating] = useState<number | undefined>(reviewPayload?.rating ?? undefined);
    const [title, setTitle] = useState<string>(reviewPayload?.title ?? '');
    const [nickname, setNickname] = useState<string | undefined>(reviewPayload?.nickName ?? defaultNickname);
    const [content, setContent] = useState<string>(reviewPayload?.content ?? '');
    const [isOpenAgeDropDown, setIsOpenAgeDropDown] = useState(false);
    const [isRecommended, setIsRecommended] = useState<boolean | undefined>(reviewPayload?.isRecommended);
    const [age, setAge] = useState<string>(reviewPayload?.age ?? '');
    const [usageDuration, setUsageDuration] = useState<string>(reviewPayload?.usageDuration ?? '');

    const submitForm = (_: unknown, formData: FormData): RatingsAndReviewsFormState => {
        formData.append(formInfo['age'].id, age);
        formData.append(formInfo['usageDuration'].id, usageDuration);
        typeof isRecommended !== 'undefined' && formData.append(formInfo['recommended'].id, isRecommended.toString());
        starRating && formData.append(formInfo['rating'].id, starRating.toString());

        const data = FIELDS_KEYS.reduce<Record<string, FormDataEntryValue>>((acc, key) => {
            const fieldId = formInfo[key].id;
            const value = formData.get(fieldId);
            if (value !== null) {
                acc[fieldId] = value;
            }
            return acc;
        }, {});

        if (!formSchema) {
            CommonDI.get(LoggerType).error(FORM_SCHEMA_NOT_INITIALIZED_ERROR, error);
            return { data, errors: undefined, success: false };
        }

        const result = formSchema.safeParse(data);

        return result.success
            ? { data: result.data, errors: undefined, success: true }
            : { data, errors: result.error.formErrors.fieldErrors as Record<string, string[]>, success: false };
    };

    const [formState, formAction] = useActionState(submitForm, initialValue);

    useEffect(() => {
        setFormSchema(
            FormUtils.generateFormSchema(
                [
                    formInfo.age,
                    formInfo.usageDuration,
                    formInfo.content,
                    formInfo.nickname,
                    formInfo.title,
                    formInfo.rating,
                    formInfo.recommended,
                ],
                {
                    [formInfo.title.id]: {
                        isRequired: formInfo.title.isRequired,
                        minLength: REVIEW_TITLE_MIN_LENGTH,
                        maxLength: REVIEW_TITLE_MAX_LENGTH,
                        regex: {
                            forbidden: FormUtils.validationRegex.sqlInjectionPattern,
                        },
                    },
                    [formInfo.nickname.id]: {
                        isRequired: formInfo.nickname.isRequired,
                        minLength: REVIEW_NICKNAME_MIN_LENGTH,
                        maxLength: REVIEW_NICKNAME_MAX_LENGTH,
                        regex: {
                            forbidden: FormUtils.validationRegex.sqlInjectionPattern,
                        },
                    },
                    [formInfo.usageDuration.id]: {
                        isRequired: formInfo.usageDuration.isRequired,
                    },
                    [formInfo.content.id]: {
                        isRequired: formInfo.content.isRequired,
                        maxLength: REVIEW_DETAILS_MAX_LENGTH,
                        regex: {
                            forbidden: FormUtils.validationRegex.sqlInjectionPattern,
                        },
                    },
                    [formInfo.rating.id]: {
                        isRequired: formInfo.rating.isRequired,
                        minValue: REVIEW_STAR_RATING_MIN_LENGTH,
                        maxValue: REVIEW_STAR_RATING_MAX_LENGTH,
                    },
                },
            ),
        );
    }, [formInfo]);

    useEffect(() => {
        onSubmit(formState?.success, {
            title: (formState?.data?.title as string) ?? '',
            content: (formState?.data?.content as string) ?? '',
            age: (formState?.data?.age as string) ?? '',
            usageDuration: (formState?.data?.usageDuration as string) ?? '',
            rating: formState?.data?.rating ? Number(formState.data.rating) : 0,
            isRecommended,
            nickName: (formState?.data?.nickname as string) ?? '',
            incentivizedReview: false, // FIXME: add this to the form for verified customers
            acceptedTermsAndConditions: true, // Form will be submitted only if acceptedTermsAndConditions's checkbox is checked
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formState, onSubmit]);

    const error = formState?.errors;

    // FIXME : This function should be moved to a domain/common file
    const getTranslatedError = (
        key: string,
        options: {
            minLength?: number;
            maxLength?: number;
            field?: string;
        } = {},
    ) => {
        const fieldError = getFieldErrorKey({
            hasError: !!error?.[key],
            type: formInfo[key].isRequired ? 'required' : undefined,
            options,
        });

        switch (fieldError?.key) {
            case 'required':
                return t('error.mandatoryField');
            case 'minValue':
                return t('error.minValue', fieldError.params);
            case 'maxValue':
                return t('error.maxValue', fieldError.params);
            default:
                return undefined;
        }
    };

    const onChangePill = (selectedId: string) => {
        setIsRecommended(selectedId === PILL_LIKE_ID);
    };

    return (
        <form action={formAction} className={'flex flex-1 flex-col'} id={RATING_REVIEWS_FORM_ID} noValidate>
            <GlowModal.Content>
                {ProductInfos}

                <GlowDivider />

                <GlowStarRating
                    fieldId={formInfo.rating.id}
                    required={formInfo.rating.isRequired}
                    errorText={getTranslatedError(formInfo['rating'].id, {
                        minLength: REVIEW_STAR_RATING_MIN_LENGTH,
                        maxLength: REVIEW_STAR_RATING_MAX_LENGTH,
                        field: starRating?.toString(),
                    })}
                    optionalText={t('label.optional')}
                    label={t('label.rating')}
                    id={formInfo.rating.id}
                    name={formInfo.rating.id}
                    onChange={setStarRating}
                    defaultValue={starRating}
                    value={starRating}
                />

                <GlowDivider />

                <fieldset className={'flex flex-col gap-1'}>
                    <GlowTextBody
                        TagName={'legend'}
                        fontWeight={'bold'}
                        text={t('label.experience')}
                        color={'primary'}
                        className={'pb-1'}
                    />

                    <div className={'flex flex-col'}>
                        <GlowInput
                            className={'block'}
                            label={t('label.ratingTitle')}
                            errorText={getTranslatedError(formInfo['title'].id, {
                                minLength: REVIEW_TITLE_MIN_LENGTH,
                                maxLength: REVIEW_TITLE_MAX_LENGTH,
                                field: title,
                            })}
                            fieldId={formInfo.title.id}
                            required={formInfo.title.isRequired}
                            optionalText={t('label.optional')}
                            helperText={t('label.titleHelper')}
                            maxLength={REVIEW_TITLE_MAX_LENGTH}
                            type={'text'}
                            id={formInfo.title.id}
                            name={formInfo.title.id}
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                        />
                    </div>

                    <GlowDropDown
                        id={formInfo.usageDuration.id}
                        optionsList={formInfo.usageDuration.options}
                        placeholderLabel={t('label.defaultSelectedValueAge')}
                        defaultSelectedValue={usageDuration}
                        title={t('label.productUseDuration')}
                        onValueUpdate={(option) => setUsageDuration(option.value)}
                        ariaLabel={t('label.productUseDuration')}
                        required={formInfo.usageDuration.isRequired}
                        optionalText={t('label.optional')}
                        errorLabel={getTranslatedError(formInfo['usageDuration'].id, {
                            field: usageDuration,
                        })}
                    />

                    <GlowTextArea
                        errorText={getTranslatedError(formInfo['content'].id, {
                            maxLength: REVIEW_DETAILS_MAX_LENGTH,
                            field: content,
                        })}
                        label={t('label.content')}
                        id={formInfo.content.id}
                        name={formInfo.content.id}
                        required={formInfo.content.isRequired}
                        maxLength={REVIEW_DETAILS_MAX_LENGTH}
                        optionalText={t('label.optional')}
                        placeholder={t('label.contentPlaceholder')}
                        value={content}
                        onChange={(e) => setContent(e.target.value)}
                    />
                </fieldset>

                <GlowDivider />

                <fieldset className={'flex flex-col gap-1'}>
                    <GlowTextBody
                        TagName={'legend'}
                        fontWeight={'bold'}
                        className={'pb-1'}
                        text={t('label.productRecommendation')}
                    />

                    <GlowPillsWrapper
                        fieldId={formInfo.recommended.id}
                        required={formInfo.recommended.isRequired}
                        type={'radio'}
                        contents={pills}
                        defaultSelected={getDefaultPillSelected(isRecommended)}
                        onChange={onChangePill}
                        className={'flex flex-1 flex-row gap-0.5 w-full'}
                        pillClassName={
                            'flex flex-1 items-center justify-center before:rounded-0.5 has-[:focus-visible]:rounded-0.5'
                        }
                        pillWrapperClassName={
                            'w-full gap-0.5 items-center justify-center !rounded-0.5 bg-surface-primary cursor-pointer'
                        }
                    />
                </fieldset>

                <GlowDivider />

                <fieldset className={'flex flex-col gap-1'}>
                    <GlowTextBody
                        TagName={'legend'}
                        fontWeight={'bold'}
                        text={t('label.information')}
                        color={'primary'}
                        className={'pb-1'}
                    />

                    <div className={'grid gap-0.5'}>
                        <GlowInput
                            label={t('label.nickname')}
                            fieldId={formInfo.nickname.id}
                            required={formInfo.nickname.isRequired}
                            optionalText={t('label.optional')}
                            type={'text'}
                            id={formInfo.nickname.id}
                            name={formInfo.nickname.id}
                            value={nickname}
                            onChange={(e) => setNickname(e.target.value)}
                            errorText={getTranslatedError(formInfo['nickname'].id, {
                                minLength: REVIEW_NICKNAME_MIN_LENGTH,
                                maxLength: REVIEW_NICKNAME_MAX_LENGTH,
                                field: nickname,
                            })}
                            helperText={t('label.nicknameHelper')}
                            maxLength={REVIEW_NICKNAME_MAX_LENGTH}
                        />
                    </div>

                    <GlowDropDown
                        id={formInfo.age.id}
                        className={clsx(isOpenAgeDropDown && 'mb-[21rem]')}
                        optionsList={formInfo.age.options}
                        defaultSelectedValue={age}
                        title={t('label.age')}
                        placeholderLabel={t('label.defaultSelectedValueAge')}
                        onValueUpdate={(option) => setAge(option.value)}
                        ariaLabel={t('label.age')}
                        required={formInfo.age.isRequired}
                        optionalText={t('label.optional')}
                        isOpenHandler={setIsOpenAgeDropDown}
                        errorLabel={getTranslatedError(formInfo['age'].id, {
                            field: age,
                        })}
                    />
                </fieldset>
            </GlowModal.Content>
            <GlowModal.Footer>
                <GlowButton
                    className={'flex-1 w-full'}
                    label={t('action.next')}
                    variant={'primary'}
                    formId={RATING_REVIEWS_FORM_ID}
                />
            </GlowModal.Footer>
        </form>
    );
};
